import React from 'react'
import { Col, Row } from 'react-styled-flexboxgrid'

import { Button, WebViewActionStrip } from '@acorns/web-components'
import { pathOr } from 'ramda'
import styled from 'styled-components'

import SelectInput, { Option } from 'components/Form/SelectInput'
import TextInput from 'components/Form/TextInput'
import Spinner from 'components/Spinner'
import { withTrackClicked } from 'components/trackers'
import AutoCompleteList from 'pages/spend/screens/signup/address/AutoCompleteList'
import { PageAnalytics } from 'utils/analytics'
import states from 'utils/states'
import { SPINNER } from 'utils/testing/testIDs'

import { CenteredContentContainer } from '../../SharedComponents'
import {
  BigText,
  ButtonsContainer,
  Container,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  FormContainer,
  GreenText,
  InfoBlock,
  LockIcon,
  LockedTextInput,
  Panel,
  PanelTitle,
  PanelTitleLink,
  PanelTitleText,
  ProgressBar,
  SSNHint,
  SSNInfoTooltip,
  SectionTitle,
} from '../../WebViewComponents'
import {
  EditButtonTapped,
  NextButtonTapped,
  SubmitButtonTapped,
} from '../segment'
import { formSubmitOverride } from '../styled-components'
import { InputAndSuggestion } from '../styled-components'
import { WVProps } from '../types'
import enhance from './enhance'

const NextButton = withTrackClicked<any, any>((props) =>
  NextButtonTapped(props.employerName),
)(Button)

const SubmitButton = withTrackClicked<any, any>((props) =>
  SubmitButtonTapped(props.employerName),
)(Button)

const EditEmployerLink = withTrackClicked<any, any>((props) =>
  EditButtonTapped(props.employerName),
)(PanelTitleLink)

export const StyledColLeft = styled(Col)`
  padding-right: 5px;
  border-right: 1px solid #f5f5f5;
`

export const StyledColRight = styled(Col)`
  padding-left: 1.2rem;
`

export const Page: React.SFC<WVProps> = (props: WVProps) => (
  <>
    {!props.data.loading && <PageAnalytics />}

    {props.data.loading && (
      <CenteredContentContainer data-testid={SPINNER}>
        <Spinner />
      </CenteredContentContainer>
    )}

    {!props.data.loading && props.editing && (
      <Container>
        <WebViewActionStrip
          title={'Employer Info'}
          action={{
            onClick: props.handleWebViewBack,
            shouldShowCloseIcon: false,
          }}
          link={{ onClick: props.handleWebViewExit, text: 'Cancel' }}
        />

        <ProgressBar progress={50} />

        <InfoBlock>
          To continue we need some additional information for{' '}
          <strong>{props.employerName}</strong>.
        </InfoBlock>

        <SectionTitle>
          Employer's Info <LockIcon />
        </SectionTitle>

        <FormContainer>
          <Row>
            <Col xs={12}>
              <LockedTextInput
                type="text"
                autocomplete="off"
                id="employerName"
                label="Name of Employer *"
                onChange={props.handleChange}
                name="employerName"
                locked={true}
                value={props.employerName}
                className="web-view-input"
              />
            </Col>
          </Row>
        </FormContainer>
        {(props.location.state.switchType !== 'submittable' ||
          props.location.state.requireAddress) && (
          <>
            <SectionTitle>Employer's Address</SectionTitle>

            <FormContainer>
              <Row>
                <Col xs={12} sm={12} md={6}>
                  <InputAndSuggestion>
                    <TextInput
                      type="text"
                      autocomplete="off"
                      autoFocus={true}
                      error={!!props.errors.address1}
                      errorText="Field Required"
                      id="address1"
                      innerRef={props.setupObservable}
                      name="address1"
                      label="Address"
                      onChange={props.handleChange}
                      required={true}
                      value={props.values.address1}
                      placeholder="123 Street"
                      className="web-view-input"
                    />
                    {props.addressAutoCompleteOptions && (
                      <AutoCompleteList
                        suggestions={props.addressAutoCompleteOptions}
                        onClickOutside={props.handleClickOutside}
                        onSelect={props.handleAutoCompleteSelect}
                      />
                    )}
                  </InputAndSuggestion>
                </Col>
                <Col xs={12} sm={12} md={6}>
                  <TextInput
                    type="text"
                    autocomplete="off"
                    id="address2"
                    label="Address (Line 2)"
                    name="address2"
                    onChange={props.handleChange}
                    required={false}
                    value={props.values.address2}
                    placeholder="Apt. #123"
                    className="web-view-input"
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={12} md={6}>
                  <TextInput
                    type="text"
                    autocomplete="off"
                    error={!!props.errors.city}
                    errorText="Field Required"
                    id="city"
                    label="City"
                    onChange={props.handleChange}
                    name="city"
                    required={true}
                    value={props.values.city}
                    placeholder="Irvine"
                    className="web-view-input"
                  />
                </Col>
              </Row>
              <Row>
                <StyledColLeft xs={6}>
                  <SelectInput
                    id="state"
                    onChange={props.handleSelectChange}
                    error={!!props.errors.state}
                    errorText="Field Required"
                    name="state"
                    label="State"
                    options={
                      states.map(({ label }) => ({
                        label,
                        value: label,
                      })) as Option[]
                    }
                    value={props.values.state}
                    useNative={true}
                    className="web-view-input last-row last-row-left"
                  />
                </StyledColLeft>
                <StyledColRight xs={6}>
                  <TextInput
                    type="text"
                    autocomplete="off"
                    error={!!props.errors.zip}
                    errorText="Field Required"
                    id="zip"
                    label="Zip"
                    onChange={props.handleChange}
                    name="zip"
                    required={true}
                    value={props.values.zip}
                    placeholder="92610"
                    className="web-view-input last-row last-row-right"
                  />
                </StyledColRight>
              </Row>
            </FormContainer>
          </>
        )}
        <SectionTitle>
          SSN <LockIcon />
        </SectionTitle>

        <FormContainer>
          <Row>
            <Col xs={12}>
              <LockedTextInput
                type="text"
                autocomplete="off"
                id="ssnLastFour"
                label="SSN *"
                onChange={props.handleChange}
                name="ssnLastFour"
                locked={true}
                value={`**** - ** - ${props.ssnLastFour}`}
                className="web-view-input"
              />
            </Col>
          </Row>
        </FormContainer>
        <SSNHint>
          All of your data is protected with 256-bit encryption and never stored
          on your phone, tablet or computer.
        </SSNHint>

        <ButtonsContainer>
          <NextButton
            onPress={props.handleNext}
            secondary={false}
            disabled={formSubmitOverride(
              pathOr(null, ['location', 'state'], props),
              props.dirty,
              props.isSubmitting,
            )}
            employerName={props.employerName}
          >
            Next
          </NextButton>
        </ButtonsContainer>
      </Container>
    )}

    {!props.data.loading && !props.editing && (
      <Container>
        <WebViewActionStrip
          title={'Review Employer Info'}
          action={{
            onClick: props.handleWebViewBack,
            shouldShowCloseIcon: false,
          }}
          link={{ onClick: props.handleWebViewExit, text: 'Cancel' }}
        />

        <ProgressBar progress={100} />

        <InfoBlock>
          Before submitting, make sure the info you provided is correct.
        </InfoBlock>

        <PanelTitle>
          <PanelTitleText>Your Employer</PanelTitleText>
          <EditEmployerLink
            onClick={props.handleEdit}
            employerName={props.employerName}
          >
            Edit Employer
          </EditEmployerLink>
        </PanelTitle>
        <Panel>
          <FieldRow>
            <Field>
              <FieldValue>
                <BigText>{props.employerName}</BigText>
              </FieldValue>
            </Field>
          </FieldRow>

          {props.values.address1 && (
            <FieldRow>
              <FieldLabel>Address</FieldLabel>
              <FieldValue>
                {props.values.address1} {props.values.address2}
                <br />
                {props.values.city}, {props.values.state} {props.values.zip}
                <br />
                United States
              </FieldValue>
            </FieldRow>
          )}

          <FieldRow>
            <FieldLabel>Deposit Percentage</FieldLabel>
            <FieldValue>
              <GreenText>100%</GreenText>
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Deposit to:</FieldLabel>
            <FieldValue>
              {'Spend Account - '}
              <GreenText>{props.balance}</GreenText>
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>SSN</FieldLabel>
            <FieldValue>
              {`**** - ** - ${props.ssnLastFour}`} <SSNInfoTooltip />
            </FieldValue>
          </FieldRow>
        </Panel>

        <ButtonsContainer>
          <SubmitButton
            onPress={props.handleSubmitSwitch}
            secondary={false}
            disabled={formSubmitOverride(
              pathOr(null, ['location', 'state'], props),
              props.dirty,
              props.isSubmitting,
            )}
            employerName={props.employerName}
          >
            Submit
          </SubmitButton>
        </ButtonsContainer>
      </Container>
    )}
  </>
)

export default enhance(Page)
